import { useEffect, useState } from "react";
import DashboardDietPlan from "@features/dashboard/DietPlanView";
import ExercisePlanView from "@features/dashboard/ExercisePlanView";
import { NoDietPlan, NoExercise } from "@utils/defaultImages";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import MainHeader from "@components/ui/MainHeader";
import HeaderImage from "@assets/images/dashboardHeader.svg";
import { useTranslation } from "react-i18next";
import SingleDayMindfulness from "@features/dashboard/Mindfulness";
import SingleDaySleepCoach from "@features/dashboard/SleeoCoachSuggestion";
import { useDashboardData } from "@hooks/useDashboardData";
import { getInitialTab } from "@utils/getInitialTab";
import EmptyPlanView from "@components/ui/EmptyPlanView";

const Home = () => {
  const { t } = useTranslation();

  const {
    loading,
    selectedMeal,
    exerciseData,
    selectedMeals,
    mindfulnessPlan,
    sleepCoach,
    setMindfulnessPlan,
  } = useDashboardData();

  const [futureExerciseDate, setFutureExerciseDate] = useState(null);

  const checkExercisePlanFuture = () => {
    if (!exerciseData?.start_time) {
      setFutureExerciseDate(null);
      return null;
    }

    const startDate = new Date(exerciseData.start_time);
    const selectedDays = exerciseData.plan_all_days || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If no selected days, return null
    if (selectedDays.length === 0) {
      setFutureExerciseDate(null);
      return null;
    }

    // Normalize day names (handle case sensitivity)
    const normalizedSelectedDays = selectedDays.map(day => 
      day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
    );

    // Look through next 7 days
    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(startDate);
      checkDate.setDate(startDate.getDate() + i);
      checkDate.setHours(0, 0, 0, 0);

      const dayName = checkDate.toLocaleDateString("en-US", {
        weekday: "long",
      });

      // Check if this day matches any selected day
      if (normalizedSelectedDays.includes(dayName)) {
        // Check if this date is in the future (including today)
        if (checkDate >= today) {
          const futureDate = {
            date: checkDate,
            day: dayName,
            formattedDate: checkDate.toLocaleDateString(),
            timestamp: checkDate.getTime()
          };
          
          setFutureExerciseDate(futureDate);
          return futureDate;
        } else {
          console.log(`Found matching day ${dayName} but date ${checkDate.toLocaleDateString()} is in the past`);
        }
      }
    }
    setFutureExerciseDate(null);
    return null;
  };

  // Call this function when exerciseData changes
  useEffect(() => {
    if (exerciseData) {
      checkExercisePlanFuture();
    }
  }, [exerciseData]);

  const [tab, setTab] = useState(() => getInitialTab());

  return (
    <div>
      {loading ? (
        <ActualDietPlanLoader dashboard={true} />
      ) : (
        <>
          <MainHeader
            heading={t(`dashboard.headerHeading`)}
            subheading={t(`dashboard.headerSubHeading`)}
            description={t(`dashboard.headerDescription`)}
            Image={HeaderImage}
          />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5">
            {/* Diet Plan Section */}
            <div className={`bg-white rounded-lg bg-[white] w-full`}>
              {selectedMeal ? (
                <DashboardDietPlan
                  tab={tab}
                  setTab={setTab}
                  selectedMeal={selectedMeal}
                  selectedMeals={selectedMeals}
                />
              ) : (
                <EmptyPlanView
                  IconComponent={<NoDietPlan />}
                  heading={t("dashboard.noDietPlanDataHeading")}
                  description={t("dashboard.noDietPlanDatadescription")}
                />
              )}
            </div>

            {/* Exercise Plan Section */}
            {exerciseData?.dayPlan ? (
              <ExercisePlanView exerciseData={exerciseData?.dayPlan} />
            ) : (
              <EmptyPlanView
                IconComponent={<NoExercise />}
                heading={futureExerciseDate ? t("dashboard.exercisePlanDataHeading") : t("dashboard.noExercisePlanDataHeading")}
                description={futureExerciseDate ? t("dashboard.noExerciseWithNextDay", { day: t(`days.${futureExerciseDate.day.toLowerCase()}`) }) : t("dashboard.noExercisePlanDatadescription")}
              />
            )}
          </div>

          {/* Mindfulness and Sleep Coach Section */}
          <div className="space-y-5 mt-5">
            <SingleDayMindfulness
              mindfulnessPlan={mindfulnessPlan}
              setMindfulnessPlan={setMindfulnessPlan}
            />
            <SingleDaySleepCoach sleepAdvice={sleepCoach} />
          </div>
        </>
      )}
    </div>
  );
};

export default Home;